import React, { useCallback, useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  ExpressCheckoutElement,
  PaymentRequestButtonElement,
  useStripe,
} from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { STRIPE_API_KEY } from "../utils";
import { postExpressPaymentIntent } from "../api/stripe";
import { isTipAlreadyPaidError } from "../utils/tipPaymentStatus";

/**
 * Pago exprés con Apple Pay / Google Pay en la primera pantalla (FormScreen).
 *
 * Usa la API clásica Payment Request de Stripe (stripe.paymentRequest +
 * PaymentRequestButtonElement, que pinta el botón oficial de Apple/Google):
 * canMakePayment() detecta la wallet sin crear ningún cobro. Solo si hay
 * Apple Pay o Google Pay se avisa al padre (onAvailabilityChange(true)) para
 * que cambie "Continuar" por este botón. Sin wallet, error o tardanza, el
 * padre deja la pantalla exactamente como estaba.
 *
 * Al pulsar: la hoja de pago se abre en el mismo gesto (sin await previo,
 * Apple lo exige). En 'paymentmethod' se guarda la propina (misma lógica que
 * "Continuar"), el backend crea el PaymentIntent con el importe calculado en
 * servidor y se confirma con confirmCardPayment (+3DS si hace falta).
 *
 * Con diagnostic=true (?express=1) muestra un panel con todo lo que pasa y
 * prueba también ExpressCheckoutElement en modo diferido, solo para comparar.
 */

let stripePromise = null;
// También la usa FormScreen para el 3DS del pago en un toque
export const getStripePromise = () => {
  if (!stripePromise) stripePromise = loadStripe(STRIPE_API_KEY);
  return stripePromise;
};

/** Si la detección de wallet tarda más, se queda la pantalla de siempre (fase 2). */
export const EXPRESS_TIMEOUT_MS = 1500;

// Importe provisional para crear el PaymentRequest antes de que el cliente
// elija importe (se actualiza con update() antes de poder abrir la hoja).
const PLACEHOLDER_AMOUNT_CENTS = 100;

const nowMs = () =>
  typeof performance !== "undefined" && performance.now
    ? performance.now()
    : Date.now();

const browserInfo = () => {
  if (typeof window === "undefined") return {};
  let applePaySession = "no existe";
  try {
    if (window.ApplePaySession) {
      applePaySession = window.ApplePaySession.canMakePayments()
        ? "canMakePayments=true"
        : "canMakePayments=false";
    }
  } catch (err) {
    applePaySession = `error: ${err?.message || err}`;
  }
  return {
    userAgent: navigator.userAgent,
    host: window.location.host,
    secureContext: window.isSecureContext,
    paymentRequestApi: typeof window.PaymentRequest === "function",
    applePaySession,
    stripeKey: (STRIPE_API_KEY || "").startsWith("pk_live")
      ? "live"
      : (STRIPE_API_KEY || "").startsWith("pk_test")
        ? "test"
        : "sin clave",
  };
};

const ExpressInner = ({
  amountCents,
  label,
  isFormValid,
  onInvalidClick,
  saveTip,
  onPaid,
  onAvailabilityChange,
  timeoutMs,
  diagnostic,
  // Otro pago en curso en la pantalla (p. ej. el pago en un toque): no abrir la hoja
  disabled = false,
  onProcessingChange,
  // Líneas extra del panel de diagnóstico: [[etiqueta, valor], ...]
  extraDiagnostics = [],
  children,
}) => {
  const stripe = useStripe();
  const { t } = useTranslation();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [diag, setDiag] = useState(() => ({
    info: browserInfo(),
    canMakePayment: "pendiente",
    elapsedMs: null,
    timedOut: false,
    buttonReady: false,
    log: [],
  }));

  const startRef = useRef(nowMs());
  const settledRef = useRef(false);
  const detectedRef = useRef(false);
  const sheetOpenRef = useRef(false);
  // Props más recientes para el handler de 'paymentmethod' (se registra una vez)
  const latestRef = useRef({});
  latestRef.current = { amountCents, label, saveTip, onPaid, isFormValid, disabled };

  // Avisar al padre mientras se cobra (para bloquear los otros botones)
  const processingChangeRef = useRef(onProcessingChange);
  processingChangeRef.current = onProcessingChange;
  useEffect(() => {
    if (processingChangeRef.current) processingChangeRef.current(processing);
  }, [processing]);

  const addLog = useCallback((msg) => {
    const ms = Math.round(nowMs() - startRef.current);
    // eslint-disable-next-line no-console
    if (diagnostic) console.log(`[express] +${ms}ms ${msg}`);
    setDiag((d) => ({ ...d, log: [...d.log, `+${ms}ms ${msg}`].slice(-40) }));
  }, [diagnostic]);

  const settle = useCallback(
    (available) => {
      if (settledRef.current) return;
      settledRef.current = true;
      onAvailabilityChange(available);
    },
    [onAvailabilityChange],
  );

  // Fallback garantizado: si la detección tarda demasiado, pantalla de siempre.
  useEffect(() => {
    if (!timeoutMs) return undefined;
    const timer = window.setTimeout(() => {
      if (settledRef.current || detectedRef.current) return;
      setDiag((d) => ({ ...d, timedOut: true }));
      addLog(`timeout ${timeoutMs}ms: se queda "Continuar"`);
      settle(false);
    }, timeoutMs);
    return () => window.clearTimeout(timer);
  }, [timeoutMs, settle, addLog]);

  // Crear el PaymentRequest y comprobar wallets (no crea ningún cobro).
  useEffect(() => {
    if (!stripe || paymentRequest) return undefined;
    addLog("stripe.js listo");
    let pr;
    try {
      pr = stripe.paymentRequest({
        country: "ES",
        currency: "eur",
        total: {
          label: latestRef.current.label,
          amount:
            latestRef.current.amountCents > 0
              ? latestRef.current.amountCents
              : PLACEHOLDER_AMOUNT_CENTS,
        },
        requestPayerName: false,
        requestPayerEmail: false,
        // Solo Apple Pay / Google Pay: ni Link ni tarjetas guardadas en el navegador
        disableWallets: ["link", "browserCard"],
      });
    } catch (err) {
      addLog(`paymentRequest() error: ${err?.message || err}`);
      setDiag((d) => ({ ...d, canMakePayment: `error: ${err?.message || err}` }));
      settle(false);
      return undefined;
    }

    let cancelled = false;
    pr.canMakePayment()
      .then((result) => {
        if (cancelled) return;
        const elapsedMs = Math.round(nowMs() - startRef.current);
        const available = !!(result && (result.applePay || result.googlePay));
        setDiag((d) => ({
          ...d,
          canMakePayment: result ? JSON.stringify(result) : "null (sin wallet)",
          elapsedMs,
        }));
        addLog(`canMakePayment → ${result ? JSON.stringify(result) : "null"}`);
        // Llegó después del timeout: no mover la pantalla bajo el dedo
        if (settledRef.current) return;
        detectedRef.current = true;
        // Con wallet, "Continuar" se cambia cuando el botón oficial esté
        // pintado (onReady); si nunca se pinta, se queda "Continuar".
        if (available) setPaymentRequest(pr);
        else settle(false);
      })
      .catch((err) => {
        if (cancelled) return;
        addLog(`canMakePayment error: ${err?.message || err}`);
        setDiag((d) => ({ ...d, canMakePayment: `error: ${err?.message || err}` }));
        settle(false);
      });

    pr.on("cancel", () => {
      sheetOpenRef.current = false;
      addLog("hoja cerrada por el cliente (cancel)");
    });

    pr.on("paymentmethod", async (ev) => {
      const { amountCents: cents, saveTip: save, onPaid: paid } = latestRef.current;
      let completed = false;
      const complete = (status) => {
        if (completed) return;
        completed = true;
        ev.complete(status);
      };
      addLog(`paymentmethod (${ev.walletName})`);
      setProcessing(true);
      setErrorMessage(null);
      try {
        const tipId = await save();
        addLog(`propina guardada ${tipId}`);
        const res = await postExpressPaymentIntent({
          tipId,
          expectedAmountCents: cents,
          wallet: ev.walletName,
        });
        const clientSecret = res?.data?.clientSecret;
        if (!clientSecret) throw new Error("Respuesta sin clientSecret");
        addLog(
          `PaymentIntent ${res.data.paymentIntentId}${res.data.reused ? " (reutilizado)" : ""}`,
        );

        const { paymentIntent, error } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false },
        );
        if (error) {
          complete("fail");
          addLog(`confirm error: ${error.code || ""} ${error.message}`);
          setErrorMessage(error.message || t("express.failed"));
          setProcessing(false);
          return;
        }
        complete("success");
        addLog(`confirm → ${paymentIntent.status}`);

        let finalStatus = paymentIntent.status;
        if (finalStatus === "requires_action") {
          // 3DS: Stripe abre el reto del banco sobre la página
          const { paymentIntent: pi2, error: error2 } =
            await stripe.confirmCardPayment(clientSecret);
          if (error2) {
            addLog(`3DS error: ${error2.message}`);
            setErrorMessage(error2.message || t("express.failed"));
            setProcessing(false);
            return;
          }
          finalStatus = pi2?.status;
          addLog(`3DS → ${finalStatus}`);
        }

        if (finalStatus === "succeeded" || finalStatus === "processing") {
          paid(tipId);
          return;
        }
        setErrorMessage(t("express.failed"));
        setProcessing(false);
      } catch (err) {
        complete("fail");
        const data = err?.response?.data;
        addLog(`error: ${data?.code || ""} ${data?.message || err?.message || err}`);
        if (isTipAlreadyPaidError(err)) {
          setErrorMessage(t("express.alreadyPaid"));
        } else if (data?.code === "AMOUNT_MISMATCH") {
          setErrorMessage(t("express.amountChanged"));
        } else {
          setErrorMessage(t("express.failed"));
        }
        setProcessing(false);
      } finally {
        sheetOpenRef.current = false;
      }
    });

    return () => {
      cancelled = true;
    };
    // Se crea una sola vez cuando stripe.js está listo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  // Mantener el total de la hoja al día (importe y corazón de costes)
  useEffect(() => {
    if (!paymentRequest || sheetOpenRef.current) return;
    if (!(amountCents > 0)) return;
    try {
      paymentRequest.update({ total: { label, amount: amountCents } });
    } catch (err) {
      addLog(`update error: ${err?.message || err}`);
    }
  }, [paymentRequest, amountCents, label, addLog]);

  const handleClick = (event) => {
    const { isFormValid: valid, amountCents: cents, disabled: off } =
      latestRef.current;
    if (processing || off) {
      event.preventDefault();
      return;
    }
    if (!valid || !(cents > 0)) {
      // Mismo comportamiento que "Continuar" con el formulario incompleto
      event.preventDefault();
      addLog("click con formulario incompleto");
      onInvalidClick();
      return;
    }
    setErrorMessage(null);
    sheetOpenRef.current = true;
    addLog(`click → hoja de pago (${cents} cént.)`);
  };

  const copyDiagnostic = () => {
    const text = JSON.stringify(diag, null, 2);
    try {
      navigator.clipboard.writeText(text);
      addLog("diagnóstico copiado");
    } catch (err) {
      addLog(`no se pudo copiar: ${err?.message || err}`);
    }
  };

  return (
    <>
      {paymentRequest && (
        <div style={{ width: "260px", maxWidth: "100%" }}>
          <div
            style={{
              opacity: isFormValid && !processing && !disabled ? 1 : 0.5,
              transition: "opacity 0.15s",
            }}
            aria-disabled={!isFormValid || processing || disabled}
          >
            <PaymentRequestButtonElement
              options={{
                paymentRequest,
                style: {
                  paymentRequestButton: {
                    type: "default",
                    theme: "dark",
                    height: "48px",
                  },
                },
              }}
              onClick={handleClick}
              onReady={() => {
                setDiag((d) => ({ ...d, buttonReady: true }));
                addLog("botón oficial pintado (ready)");
                settle(true);
              }}
            />
          </div>
          {processing && (
            <p className="form-express-status">{t("express.processing")}</p>
          )}
          {errorMessage && (
            <p className="form-express-error" role="alert">
              {errorMessage}
            </p>
          )}
        </div>
      )}
      {children}
      {diagnostic && (
        <ExpressDiagnosticPanel
          diag={diag}
          amountCents={amountCents}
          extra={extraDiagnostics}
          onCopy={copyDiagnostic}
        />
      )}
    </>
  );
};

/** Prueba de ExpressCheckoutElement en modo diferido (solo diagnóstico, no cobra). */
const ExpressCheckoutProbe = ({ amountCents }) => {
  const [state, setState] = useState("cargando…");
  return (
    <div style={{ marginTop: 8 }}>
      <div>
        <b>ExpressCheckoutElement (diferido):</b> {state}
      </div>
      <Elements
        stripe={getStripePromise()}
        options={{
          mode: "payment",
          amount: amountCents > 0 ? amountCents : PLACEHOLDER_AMOUNT_CENTS,
          currency: "eur",
          paymentMethodTypes: ["card"],
        }}
      >
        <ExpressCheckoutElement
          options={{
            paymentMethods: { link: "never", amazonPay: "never", paypal: "never" },
          }}
          onReady={({ availablePaymentMethods }) =>
            setState(
              `ready → ${
                availablePaymentMethods
                  ? JSON.stringify(availablePaymentMethods)
                  : "sin métodos (no pinta nada)"
              }`,
            )
          }
          onLoadError={(e) =>
            setState(`loaderror → ${e?.error?.message || JSON.stringify(e)}`)
          }
          onClick={() => setState((s) => `${s} · click (solo diagnóstico, no abre)`)}
          onConfirm={() => {}}
        />
      </Elements>
    </div>
  );
};

const ExpressDiagnosticPanel = ({ diag, amountCents, extra = [], onCopy }) => (
  <div
    style={{
      marginTop: 12,
      width: "100%",
      maxWidth: 420,
      padding: 10,
      border: "1px dashed #999",
      borderRadius: 8,
      background: "#fafafa",
      fontSize: 11,
      lineHeight: 1.4,
      textAlign: "left",
      wordBreak: "break-word",
      color: "#333",
    }}
  >
    <div style={{ fontWeight: 700, marginBottom: 4 }}>
      Diagnóstico pago exprés (?express=1)
    </div>
    <div>
      <b>canMakePayment:</b> {diag.canMakePayment}
      {diag.elapsedMs != null ? ` (${diag.elapsedMs} ms)` : ""}
      {diag.timedOut ? " · superó el timeout" : ""}
      {diag.elapsedMs != null && diag.elapsedMs > EXPRESS_TIMEOUT_MS
        ? ` · en modo normal (>${EXPRESS_TIMEOUT_MS} ms) no se habría mostrado`
        : ""}
    </div>
    <div>
      <b>Botón oficial pintado:</b> {diag.buttonReady ? "sí" : "no"}
    </div>
    <div>
      <b>Importe actual:</b> {amountCents > 0 ? `${amountCents} cént.` : "sin importe"}
    </div>
    {extra.map(([label, value]) => (
      <div key={label}>
        <b>{label}:</b> {value}
      </div>
    ))}
    <div>
      <b>Stripe:</b> {diag.info.stripeKey} · <b>host:</b> {diag.info.host} ·{" "}
      <b>https:</b> {String(diag.info.secureContext)}
    </div>
    <div>
      <b>PaymentRequest API:</b> {String(diag.info.paymentRequestApi)} ·{" "}
      <b>ApplePaySession:</b> {diag.info.applePaySession}
    </div>
    <div>
      <b>Navegador:</b> {diag.info.userAgent}
    </div>
    <ExpressCheckoutProbe amountCents={amountCents} />
    <div style={{ marginTop: 6 }}>
      <b>Eventos:</b>
      {diag.log.map((line, i) => (
        <div key={i} style={{ fontFamily: "monospace" }}>
          {line}
        </div>
      ))}
    </div>
    <button type="button" onClick={onCopy} style={{ marginTop: 6, fontSize: 12 }}>
      Copiar diagnóstico
    </button>
  </div>
);

const WalletExpressPay = (props) => {
  return (
    <Elements stripe={getStripePromise()} options={{ locale: "auto" }}>
      <ExpressInner {...props} />
    </Elements>
  );
};

export default WalletExpressPay;
